/**
 * GmailLabelsPicker – fetches available Gmail labels and lets the user select
 * which ones to monitor. Saves via POST to the backend.
 */
import React, { useEffect, useState } from 'react';
import { useGetGmailAvailableLabelsQuery, useSaveGmailLabelsMutation } from '@/store/emailInboxApi';
import { GmailAvailableLabel } from '@/types/EmailInbox';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Spinner } from '@/components/ui/spinner';
import { toast } from 'sonner';
import { RefreshCw, Save } from 'lucide-react';

interface Props {
  inboxId: number;
}

export function GmailLabelsPicker({ inboxId }: Props) {
  const {
    data: availableLabels,
    isLoading,
    isError,
    refetch,
  } = useGetGmailAvailableLabelsQuery(inboxId);

  const [saveLabels, { isLoading: isSaving }] = useSaveGmailLabelsMutation();

  // Local selected state: label_id → boolean
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [isDirty, setIsDirty] = useState(false);

  // Initialise from API response (respects already-saved labels via `selected` flag)
  useEffect(() => {
    if (availableLabels) {
      const initial: Record<string, boolean> = {};
      availableLabels.forEach((lbl) => {
        initial[lbl.id] = lbl.selected;
      });
      setSelected(initial);
      setIsDirty(false);
    }
  }, [availableLabels]);

  function toggle(labelId: string, checked: boolean) {
    setSelected((prev) => ({ ...prev, [labelId]: checked }));
    setIsDirty(true);
  }

  async function handleSave() {
    if (!availableLabels) return;
    const labelsToSave = availableLabels
      .filter((lbl) => selected[lbl.id])
      .map((lbl) => ({ id: lbl.id, name: lbl.name }));

    try {
      await saveLabels({ inboxId, labels: labelsToSave }).unwrap();
      toast.success('הLabels נשמרו בהצלחה');
      setIsDirty(false);
    } catch {
      toast.error('שגיאה בשמירת Labels');
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-gray-500">
        <Spinner className="h-4 w-4" />
        טוען Labels מ-Gmail...
      </div>
    );
  }

  if (isError || !availableLabels) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        <p>שגיאה בטעינת Labels.</p>
        <Button type="button" variant="ghost" size="sm" onClick={() => refetch()} className="mt-1 gap-1">
          <RefreshCw className="h-3 w-3" />
          נסה שוב
        </Button>
      </div>
    );
  }

  const systemLabels = availableLabels.filter((l) => l.type === 'system');
  const userLabels = availableLabels.filter((l) => l.type === 'user');

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-gray-700">תיקיות / Labels לניטור</p>
        <Button type="button" variant="ghost" size="sm" onClick={() => refetch()} className="gap-1 h-7 text-xs">
          <RefreshCw className="h-3 w-3" />
          רענן
        </Button>
      </div>

      <div className="border rounded-md divide-y max-h-72 overflow-y-auto">
        {systemLabels.length > 0 && (
          <div>
            <p className="px-3 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50 uppercase tracking-wide">
              תיקיות מערכת
            </p>
            {systemLabels.map((lbl) => (
              <LabelRow key={lbl.id} label={lbl} checked={!!selected[lbl.id]} onChange={toggle} />
            ))}
          </div>
        )}

        {userLabels.length > 0 && (
          <div>
            <p className="px-3 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50 uppercase tracking-wide">
              Labels מותאמות אישית
            </p>
            {userLabels.map((lbl) => (
              <LabelRow key={lbl.id} label={lbl} checked={!!selected[lbl.id]} onChange={toggle} />
            ))}
          </div>
        )}

        {availableLabels.length === 0 && (
          <p className="px-3 py-4 text-sm text-gray-400 text-center">לא נמצאו Labels בחשבון</p>
        )}
      </div>

      <Button
        type="button"
        size="sm"
        disabled={!isDirty || isSaving}
        onClick={handleSave}
        className="gap-2 w-full"
      >
        {isSaving ? <Spinner className="h-4 w-4" /> : <Save className="h-4 w-4" />}
        שמור Labels לניטור
      </Button>
    </div>
  );
}

function LabelRow({
  label,
  checked,
  onChange,
}: {
  label: GmailAvailableLabel;
  checked: boolean;
  onChange: (id: string, checked: boolean) => void;
}) {
  return (
    <label
      className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 cursor-pointer select-none"
    >
      <Checkbox
        checked={checked}
        onCheckedChange={(v) => onChange(label.id, !!v)}
      />
      <span className="text-sm text-gray-800 flex-1">{label.name}</span>
      <span className="text-xs text-gray-400 font-mono">{label.id}</span>
    </label>
  );
}
